import React, { useState } from 'react';
import { Plus, Search, Eye, Edit2, Trash2, Printer, ArrowDownLeft, FileText, CheckCircle, Clock, Calendar, X, RotateCcw, Filter, Truck } from 'lucide-react';
import CustomDatePicker from '../../shared/CustomDatePicker';
import { formatWarehouseDate } from '../../../../../utils/dateUtils';

const getTodayIso = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const MONTH_MAP = { jan: '01', feb: '02', mar: '03', apr: '04', may: '05', jun: '06', jul: '07', aug: '08', sep: '09', oct: '10', nov: '11', dec: '12' };

const getIsoDateStr = (dateVal) => {
    if (!dateVal) return '';
    if (typeof dateVal !== 'string') dateVal = String(dateVal);
    const trimmed = dateVal.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
    if (/^\d{4}-\d{2}-\d{2}T/.test(trimmed)) return trimmed.split('T')[0];

    const match = trimmed.match(/^(\d{1,2})[\s\-]+([A-Za-z]{3})[\s\-]+(\d{4})/);
    if (match) {
        const day = match[1].padStart(2, '0');
        const monthStr = match[2].toLowerCase();
        const month = MONTH_MAP[monthStr] || '01';
        const year = match[3];
        return `${year}-${month}-${day}`;
    }

    try {
        const parsed = new Date(trimmed);
        if (!isNaN(parsed.getTime())) {
            const year = parsed.getFullYear();
            const month = String(parsed.getMonth() + 1).padStart(2, '0');
            const day = String(parsed.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        }
    } catch (e) {}
    return '';
};

const OffloadRequestsView = ({
    requests = [],
    currentRole,
    onCreateNew,
    onViewRequest,
    onEditRequest,
    onPrintPdf,
    onDeleteDraft,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('ALL');
    const [dateFilterMode, setDateFilterMode] = useState('ALL'); // Default to ALL dates so list items match summary card counts
    const [customDate, setCustomDate] = useState(getTodayIso());

    const handleCardStatusClick = (status) => {
        setStatusFilter(status);
    };

    const effectiveDateFilter = dateFilterMode === 'TODAY' ? getTodayIso() : (dateFilterMode === 'CUSTOM' ? customDate : '');

    // Filter requests by active date range first so summary cards match selected date range
    const dateFilteredRequests = requests.filter(req => {
        if (!effectiveDateFilter) return true;
        const reqDateIso = req.requestDate || req.createdDateIso || getIsoDateStr(req.date);
        return reqDateIso === effectiveDateFilter;
    });

    // Dynamic counts calculated directly from date-filtered requests list
    const totalCount = dateFilteredRequests.length;
    const pendingCount = dateFilteredRequests.filter(r => {
        const s = (r.status || '').toUpperCase();
        return s === 'SUBMITTED' || s === 'PENDING RETURN' || s === 'PENDING';
    }).length;
    const verificationCount = dateFilteredRequests.filter(r => {
        const s = (r.status || '').toUpperCase();
        return s === 'UNDER VERIFICATION' || s === 'VERIFYING';
    }).length;
    const completedCount = dateFilteredRequests.filter(r => {
        const s = (r.status || '').toUpperCase();
        return s === 'COMPLETED' || s === 'STOCK RECEIVED' || s === 'RECEIVED';
    }).length;

    const filteredRequests = dateFilteredRequests.filter(req => {
        const term = searchTerm.toLowerCase();
        const matchesSearch = !searchTerm ||
            (req.requestNo && req.requestNo.toLowerCase().includes(term)) ||
            (req.salesperson && req.salesperson.toLowerCase().includes(term)) ||
            (req.salespersonName && req.salespersonName.toLowerCase().includes(term)) ||
            (req.vehicle && req.vehicle.toLowerCase().includes(term)) ||
            (req.items || []).some(i => (i.productName || i.name || i.product || '').toLowerCase().includes(term));

        const s = (req.status || '').toUpperCase();
        let matchesStatus = true;
        if (statusFilter === 'DRAFT') {
            matchesStatus = s === 'DRAFT';
        } else if (statusFilter === 'SUBMITTED') {
            matchesStatus = s === 'SUBMITTED' || s === 'PENDING RETURN' || s === 'PENDING';
        } else if (statusFilter === 'VERIFICATION') {
            matchesStatus = s === 'UNDER VERIFICATION' || s === 'VERIFYING';
        } else if (statusFilter === 'COMPLETED') {
            matchesStatus = s === 'COMPLETED' || s === 'STOCK RECEIVED' || s === 'RECEIVED';
        }

        return matchesSearch && matchesStatus;
    });

    const isFiltered = searchTerm || statusFilter !== 'ALL' || dateFilterMode !== 'ALL';

    const handleResetFilters = () => {
        setSearchTerm('');
        setStatusFilter('ALL');
        setDateFilterMode('ALL');
    };

    const getStatusBadge = (status) => {
        switch (status?.toUpperCase()) {
            case 'DRAFT':
                return <span className="px-2.5 py-1 text-xs rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 font-semibold border border-slate-200 dark:border-slate-600">Draft</span>;
            case 'SUBMITTED':
            case 'PENDING RETURN':
            case 'PENDING':
                return <span className="px-2.5 py-1 text-xs rounded-full bg-amber-50 dark:bg-amber-900/40 text-amber-600 dark:text-amber-300 font-semibold border border-amber-200 dark:border-amber-700">Pending Return</span>;
            case 'UNDER VERIFICATION':
            case 'VERIFICATION':
                return <span className="px-2.5 py-1 text-xs rounded-full bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 font-semibold border border-blue-200 dark:border-blue-700">Under Verification</span>;
            case 'STOCK RECEIVED':
            case 'COMPLETED':
            case 'RECEIVED':
                return <span className="px-2.5 py-1 text-xs rounded-full bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 font-semibold border border-emerald-200 dark:border-emerald-700">Stock Received</span>;
            default:
                return <span className="px-2.5 py-1 text-xs rounded-full bg-slate-100 text-slate-600 font-semibold">{status}</span>;
        }
    };

    return (
        <div className="space-y-5">
            {/* Top Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-xl font-extrabold text-slate-800 dark:text-white flex items-center gap-2 tracking-tight">
                        <div className="w-8 h-8 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-md shadow-blue-500/20">
                            <ArrowDownLeft className="w-5 h-5" />
                        </div>
                        Offload Request
                    </h1>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-1">
                        Exception return process for damaged, expired, slow-moving, or wrong items
                    </p>
                </div>
                {currentRole !== 'WAREHOUSE_PERSON' && (
                    <button
                        onClick={onCreateNew}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 active:scale-[0.98] text-white rounded-xl font-bold text-xs transition-all shadow-md shadow-blue-500/25 cursor-pointer"
                    >
                        <Plus className="w-4 h-4" /> New Offload Request
                    </button>
                )}
            </div>

            {/* Dynamic Stat Cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div
                    onClick={() => handleCardStatusClick('ALL')}
                    className={`bg-white dark:bg-slate-800 p-3.5 rounded-2xl border transition-all cursor-pointer ${
                        statusFilter === 'ALL'
                            ? 'border-blue-500 ring-2 ring-blue-500/20 shadow-md'
                            : 'border-slate-100 dark:border-slate-700/80 hover:border-blue-300'
                    } flex items-center gap-3`}
                >
                    <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                        <FileText className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-lg sm:text-xl font-black text-slate-800 dark:text-white leading-tight">{totalCount}</div>
                        <div className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Total Requests</div>
                    </div>
                </div>

                <div
                    onClick={() => handleCardStatusClick('SUBMITTED')}
                    className={`bg-white dark:bg-slate-800 p-3.5 rounded-2xl border transition-all cursor-pointer ${
                        statusFilter === 'SUBMITTED'
                            ? 'border-amber-500 ring-2 ring-amber-500/20 shadow-md'
                            : 'border-slate-100 dark:border-slate-700/80 hover:border-amber-300'
                    } flex items-center gap-3`}
                >
                    <div className="w-10 h-10 rounded-xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-lg sm:text-xl font-black text-slate-800 dark:text-white leading-tight">{pendingCount}</div>
                        <div className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Pending Return</div>
                    </div>
                </div>

                <div
                    onClick={() => handleCardStatusClick('VERIFICATION')}
                    className={`bg-white dark:bg-slate-800 p-3.5 rounded-2xl border transition-all cursor-pointer ${
                        statusFilter === 'VERIFICATION'
                            ? 'border-blue-500 ring-2 ring-blue-500/20 shadow-md'
                            : 'border-slate-100 dark:border-slate-700/80 hover:border-blue-300'
                    } flex items-center gap-3`}
                >
                    <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center flex-shrink-0">
                        <ArrowDownLeft className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-lg sm:text-xl font-black text-slate-800 dark:text-white leading-tight">{verificationCount}</div>
                        <div className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Verification</div>
                    </div>
                </div>

                <div
                    onClick={() => handleCardStatusClick('COMPLETED')}
                    className={`bg-white dark:bg-slate-800 p-3.5 rounded-2xl border transition-all cursor-pointer ${
                        statusFilter === 'COMPLETED'
                            ? 'border-emerald-500 ring-2 ring-emerald-500/20 shadow-md'
                            : 'border-slate-100 dark:border-slate-700/80 hover:border-emerald-300'
                    } flex items-center gap-3`}
                >
                    <div className="w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-lg sm:text-xl font-black text-slate-800 dark:text-white leading-tight">{completedCount}</div>
                        <div className="text-[11px] font-semibold text-slate-500 dark:text-slate-400">Stock Received</div>
                    </div>
                </div>
            </div>

            {/* Filter Toolbar */}
            <div className="bg-white dark:bg-slate-800 p-3.5 sm:p-4 rounded-2xl border border-slate-200/80 dark:border-slate-700 shadow-sm space-y-3">
                <div className="flex flex-col lg:flex-row items-stretch lg:items-center gap-3">
                    {/* Search Input */}
                    <div className="relative flex-1">
                        <Search className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            placeholder="Search offload request no, vehicle, salesperson..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-9 h-11 text-xs bg-slate-50 dark:bg-slate-700/60 rounded-xl border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white font-medium placeholder-slate-400 transition-all"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-white"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                    {/* Status Dropdown */}
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-3.5 h-11 text-xs bg-slate-50 dark:bg-slate-700/60 rounded-xl border border-slate-200 dark:border-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500 text-slate-800 dark:text-white font-semibold transition-all cursor-pointer"
                    >
                        <option value="ALL">Status: All</option>
                        <option value="DRAFT">Draft</option>
                        <option value="SUBMITTED">Pending Return</option>
                        <option value="VERIFICATION">Under Verification</option>
                        <option value="COMPLETED">Completed / Stock Received</option>
                    </select>

                    {/* Quick Date Filter Pills */}
                    <div className="flex items-center bg-slate-100 dark:bg-slate-700/80 p-1 rounded-xl gap-1 overflow-x-auto no-scrollbar">
                        <button
                            type="button"
                            onClick={() => setDateFilterMode('TODAY')}
                            className={`px-3.5 h-9 rounded-lg text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 cursor-pointer ${
                                dateFilterMode === 'TODAY'
                                    ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900'
                            }`}
                        >
                            <Calendar className="w-3.5 h-3.5" /> Today
                        </button>
                        <button
                            type="button"
                            onClick={() => setDateFilterMode('ALL')}
                            className={`px-3.5 h-9 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                                dateFilterMode === 'ALL'
                                    ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900'
                            }`}
                        >
                            All Dates
                        </button>
                        <button
                            type="button"
                            onClick={() => setDateFilterMode('CUSTOM')}
                            className={`px-3.5 h-9 rounded-lg text-xs font-bold transition-all whitespace-nowrap cursor-pointer ${
                                dateFilterMode === 'CUSTOM'
                                    ? 'bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 shadow-sm'
                                    : 'text-slate-600 dark:text-slate-300 hover:text-slate-900'
                            }`}
                        >
                            Custom
                        </button>
                    </div>

                    {/* Custom Date Input */}
                    {dateFilterMode === 'CUSTOM' && (
                        <CustomDatePicker
                            mode="single"
                            value={customDate}
                            onChange={(val) => setCustomDate(val)}
                            quickFilters={false}
                        />
                    )}
                </div>

                {/* Filter Status Tag & Reset */}
                {isFiltered && (
                    <div className="flex items-center justify-between pt-2 border-t border-slate-100 dark:border-slate-700 text-xs">
                        <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400 font-medium">
                            <Filter className="w-3.5 h-3.5 text-blue-500" />
                            <span>Showing results for:</span>
                            <span className="font-bold text-slate-800 dark:text-white bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-md">
                                {dateFilterMode === 'TODAY' ? `Today (${getTodayIso()})` : dateFilterMode === 'CUSTOM' ? `Date: ${customDate}` : 'All Dates'}
                            </span>
                            {statusFilter !== 'ALL' && (
                                <span className="font-bold text-slate-800 dark:text-white bg-slate-100 dark:bg-slate-700 px-2 py-0.5 rounded-md">
                                    Status: {statusFilter}
                                </span>
                            )}
                        </div>
                        <button
                            onClick={handleResetFilters}
                            className="text-xs text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 font-semibold cursor-pointer"
                        >
                            <RotateCcw className="w-3 h-3" /> Clear Filters
                        </button>
                    </div>
                )}
            </div>

            {/* Content Container */}
            <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200/80 dark:border-slate-700 shadow-sm overflow-hidden">
                {/* Empty State */}
                {filteredRequests.length === 0 ? (
                    <div className="p-10 text-center space-y-3">
                        <div className="w-12 h-12 rounded-2xl bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center mx-auto">
                            <ArrowDownLeft className="w-6 h-6" />
                        </div>
                        <div>
                            <h3 className="font-bold text-sm text-slate-800 dark:text-white">No Offload Requests Found</h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 max-w-sm mx-auto">
                                {dateFilterMode === 'TODAY'
                                    ? `There are no offload requests logged for today (${getTodayIso()}).`
                                    : 'No offload requests match your current search and filter criteria.'}
                            </p>
                        </div>
                        <div className="flex items-center justify-center gap-3 pt-2">
                            {dateFilterMode === 'TODAY' && (
                                <button
                                    onClick={() => setDateFilterMode('ALL')}
                                    className="px-4 py-2 rounded-xl bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 text-slate-700 dark:text-slate-200 text-xs font-bold transition-all cursor-pointer"
                                >
                                    View All Dates
                                </button>
                            )}
                            <button
                                onClick={onCreateNew}
                                className="px-4 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold transition-all cursor-pointer shadow-sm"
                            >
                                + New Offload Request
                            </button>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Mobile PWA Cards */}
                        <div className="block md:hidden p-3 space-y-3">
                            {filteredRequests.map((req) => (
                                <div
                                    key={req.id}
                                    className="p-4 rounded-xl border border-slate-200/80 dark:border-slate-700 bg-gradient-to-b from-white to-slate-50/50 dark:from-slate-800 dark:to-slate-800/80 shadow-sm space-y-3"
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div>
                                            <div className="font-extrabold text-sm text-blue-600 dark:text-blue-400 flex items-center gap-1.5">
                                                <span>{req.requestNo}</span>
                                            </div>
                                            <div className="text-[11px] font-medium text-slate-500 dark:text-slate-400 flex items-center gap-1 mt-0.5">
                                                <Calendar className="w-3 h-3 text-slate-400" /> {formatWarehouseDate(req.date || req.requestDate)}
                                            </div>
                                        </div>
                                        <div>{getStatusBadge(req.status)}</div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-2 pt-2 border-t border-slate-100 dark:border-slate-700/60 text-xs">
                                        <div>
                                            <span className="text-[10px] uppercase font-extrabold text-slate-400 block tracking-wider">Salesperson</span>
                                            <span className="font-bold text-slate-800 dark:text-slate-200">{req.salesperson}</span>
                                        </div>
                                        <div>
                                            <span className="text-[10px] uppercase font-extrabold text-slate-400 block tracking-wider">Vehicle</span>
                                            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-lg bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 font-bold text-[11px] border border-blue-100 dark:border-blue-800">
                                                    <Truck className="w-3 h-3" /> {req.vehicle || 'Van 01'}
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-[10px] uppercase font-extrabold text-slate-400 block tracking-wider">Items Count</span>
                                            <span className="font-bold text-slate-700 dark:text-slate-300">{req.itemsCount} items</span>
                                        </div>
                                        <div>
                                            <span className="text-[10px] uppercase font-extrabold text-slate-400 block tracking-wider">Total Quantity</span>
                                            <span className="font-black text-slate-900 dark:text-white text-sm">{req.totalQty}</span>
                                        </div>
                                    </div>

                                    {/* Action Buttons (Unified Theme with Loading Requests View) */}
                                    <div className="flex items-center gap-2 pt-2 border-t border-slate-100 dark:border-slate-700/60 flex-wrap">
                                        <button
                                            onClick={() => onViewRequest(req)}
                                            className="h-10 px-3.5 rounded-xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 font-bold text-xs hover:bg-slate-800 dark:hover:bg-slate-100 active:scale-95 transition-all flex items-center gap-1.5 cursor-pointer shadow-xs"
                                        >
                                            <Eye className="w-4 h-4" /> View Details
                                        </button>

                                        {['Submitted', 'SUBMITTED', 'Pending Return', 'PENDING RETURN', 'Pending', 'PENDING'].includes(req.status) && (
                                            <button
                                                onClick={() => onViewRequest(req)}
                                                className="h-10 px-3 rounded-xl border border-blue-200 text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 font-bold text-xs flex items-center gap-1.5 cursor-pointer active:scale-95 transition-all"
                                            >
                                                <CheckCircle className="w-4 h-4" /> Verify Offload
                                            </button>
                                        )}

                                        {['Draft', 'DRAFT', 'Submitted', 'SUBMITTED', 'Pending Return', 'PENDING RETURN', 'Pending', 'PENDING'].includes(req.status) && (
                                            <button
                                                onClick={() => onEditRequest(req)}
                                                className="h-10 px-3.5 rounded-xl border border-amber-200 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 font-bold text-xs flex items-center gap-1.5 cursor-pointer active:scale-95 transition-all"
                                            >
                                                <Edit2 className="w-4 h-4" /> Edit
                                            </button>
                                        )}

                                        <button
                                            onClick={() => onPrintPdf(req)}
                                            className="h-10 px-3.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 font-bold text-xs hover:bg-slate-100 dark:hover:bg-slate-700 active:scale-95 transition-all flex items-center gap-1.5 cursor-pointer"
                                        >
                                            <Printer className="w-4 h-4 text-slate-500" /> PDF
                                        </button>

                                        {(req.status === 'Draft' || req.status === 'DRAFT') && onDeleteDraft && (
                                            <button
                                                onClick={() => onDeleteDraft(req.id)}
                                                className="h-10 px-3.5 rounded-xl border border-rose-200 text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-900/20 font-bold text-xs flex items-center gap-1.5 cursor-pointer active:scale-95 transition-all"
                                            >
                                                <Trash2 className="w-4 h-4" /> Delete
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Desktop Table View */}
                        <div className="hidden md:block overflow-x-auto">
                            <table className="w-full text-left text-xs">
                                <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 font-extrabold border-b border-slate-100 dark:border-slate-700 uppercase tracking-wider text-[11px]">
                                    <tr>
                                        <th className="p-4 pl-6">Request No.</th>
                                        <th className="p-4">Date</th>
                                        <th className="p-4">Salesperson</th>
                                        <th className="p-4">Vehicle</th>
                                        <th className="p-4">No. of Items</th>
                                        <th className="p-4">Total Qty</th>
                                        <th className="p-4">Status</th>
                                        <th className="p-4 text-center">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                                    {filteredRequests.map((req) => (
                                        <tr key={req.id} className="hover:bg-slate-50/70 dark:hover:bg-slate-700/40 transition-colors">
                                            <td className="p-4 pl-6 font-extrabold text-blue-600 dark:text-blue-400">{req.requestNo}</td>
                                            <td className="p-4 text-slate-600 dark:text-slate-300 font-medium">{formatWarehouseDate(req.date || req.requestDate)}</td>
                                            <td className="p-4 text-slate-800 dark:text-white font-bold">{req.salesperson}</td>
                                            <td className="p-4">
                                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 font-bold text-[11px] border border-blue-100 dark:border-blue-800">
                                                        <Truck className="w-3 h-3" /> {req.vehicle || 'Van 01'}
                                                </span>
                                            </td>
                                            <td className="p-4 text-slate-600 dark:text-slate-300 font-semibold">{req.itemsCount}</td>
                                            <td className="p-4 font-black text-slate-800 dark:text-white">{req.totalQty}</td>
                                            <td className="p-4">{getStatusBadge(req.status)}</td>
                                            <td className="p-4">
                                                <div className="flex items-center justify-center gap-2">
                                                    <button
                                                        onClick={() => onViewRequest(req)}
                                                        className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-blue-600 transition-colors flex items-center justify-center cursor-pointer"
                                                        title="View Request"
                                                    >
                                                        <Eye className="w-4 h-4" />
                                                    </button>

                                                    {['Submitted', 'SUBMITTED', 'Pending Return', 'PENDING RETURN', 'Pending', 'PENDING'].includes(req.status) && (
                                                        <button
                                                            onClick={() => onViewRequest(req)}
                                                            className="px-2.5 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-300 font-bold text-[11px] flex items-center gap-1 cursor-pointer transition-colors border border-blue-200 dark:border-blue-800"
                                                            title="Verify Offload"
                                                        >
                                                            <CheckCircle className="w-3 h-3" /> Verify Offload
                                                        </button>
                                                    )}

                                                    {['Draft', 'DRAFT', 'Submitted', 'SUBMITTED', 'Pending Return', 'PENDING RETURN', 'Pending', 'PENDING'].includes(req.status) && (
                                                        <button
                                                            onClick={() => onEditRequest(req)}
                                                            className="w-8 h-8 rounded-lg border border-amber-200 text-amber-600 hover:bg-amber-50 transition-colors flex items-center justify-center cursor-pointer"
                                                            title="Edit Request"
                                                        >
                                                            <Edit2 className="w-4 h-4" />
                                                        </button>
                                                    )}

                                                    <button
                                                        onClick={() => onPrintPdf(req)}
                                                        className="w-8 h-8 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors flex items-center justify-center cursor-pointer"
                                                        title="Print PDF"
                                                    >
                                                        <Printer className="w-4 h-4" />
                                                    </button>

                                                    {(req.status === 'Draft' || req.status === 'DRAFT') && onDeleteDraft && (
                                                        <button
                                                            onClick={() => onDeleteDraft(req.id)}
                                                            className="w-8 h-8 rounded-lg border border-rose-200 text-rose-600 hover:bg-rose-50 transition-colors flex items-center justify-center cursor-pointer"
                                                            title="Delete Draft"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default OffloadRequestsView;
